import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { LogOut, User, Trophy, Receipt, FilePlus, FileText, Store, ShoppingBag, Shield, Crown, Gem, Users, Zap, Target, Download } from 'lucide-react';
import { Session } from '@supabase/supabase-js';
import { ProfileModal } from '@/components/ProfileModal';
import { PointsStatementModal } from '@/components/modals/PointsStatementModal';
import { PointsEarningRequestModal } from '@/components/modals/PointsEarningRequestModal';
import { PointsEarningHistoryModal } from '@/components/modals/PointsEarningHistoryModal';
import { RewardsStoreModal } from '@/components/modals/RewardsStoreModal';
import { RewardsHistoryModal } from '@/components/modals/RewardsHistoryModal';
import { LevelInfoModal } from '@/components/modals/LevelInfoModal';
import { ReferralModal } from '@/components/modals/ReferralModal';
import { HowToEarnPointsModal } from '@/components/modals/HowToEarnPointsModal';
import { WeeklyMissionsModal } from '@/components/modals/WeeklyMissionsModal';
import { RadarLevel } from '@/types';

interface UserMenuProps {
    session: Session;
    isMobile: boolean;
}

const levelConfig: Record<RadarLevel, { color: string, textColor: string, icon: React.ReactNode }> = {
    'bronze': {
        color: 'from-amber-700 to-amber-900',
        textColor: 'text-amber-700 dark:text-amber-500',
        icon: <Shield className="w-4 h-4" />
    },
    'prata': {
        color: 'from-slate-300 to-slate-500',
        textColor: 'text-slate-600 dark:text-slate-300',
        icon: <Trophy className="w-4 h-4" />
    },
    'ouro': {
        color: 'from-yellow-400 to-yellow-600',
        textColor: 'text-yellow-600 dark:text-yellow-400',
        icon: <Crown className="w-4 h-4" />
    },
    'diamante': {
        color: 'from-cyan-300 to-blue-500',
        textColor: 'text-blue-600 dark:text-blue-400',
        icon: <Gem className="w-4 h-4" />
    }
};

export const UserMenu = ({ session, isMobile }: UserMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isStatementOpen, setIsStatementOpen] = useState(false);
    const [isPointsRequestOpen, setIsPointsRequestOpen] = useState(false);
    const [isPointsHistoryOpen, setIsPointsHistoryOpen] = useState(false);
    const [isStoreOpen, setIsStoreOpen] = useState(false);
    const [isRewardsHistoryOpen, setIsRewardsHistoryOpen] = useState(false);
    const [isLevelInfoOpen, setIsLevelInfoOpen] = useState(false);
    const [isReferralOpen, setIsReferralOpen] = useState(false);
    const [isHowToEarnPointsOpen, setIsHowToEarnPointsOpen] = useState(false);
    const [isWeeklyMissionsOpen, setIsWeeklyMissionsOpen] = useState(false);
    const [displayName, setDisplayName] = useState<string | null>(null);
    const [level, setLevel] = useState<RadarLevel>('bronze');
    const [levelExpiresAt, setLevelExpiresAt] = useState<string | null>(null);
    const [downgradedAt, setDowngradedAt] = useState<string | null>(null);
    const [points, setPoints] = useState<number | null>(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [canInstall, setCanInstall] = useState(false);

    const menuRef = useRef<HTMLDivElement>(null);
    const lastFetchedIdRef = useRef<string | null>(null);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // PWA Install Event
    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setCanInstall(true);
        };

        window.addEventListener('beforeinstallprompt', handler);
        const standalone = window.matchMedia('(display-mode: standalone)').matches;
        const iosStandalone = (window.navigator as any).standalone === true;

        // Se o a pessoa chegou da App, o display-mode é 'standalone'
        if (standalone || iosStandalone) {
            setCanInstall(false); // Já está instalado e usado pelo App
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    useEffect(() => {
        if (lastFetchedIdRef.current === session.user.id) return;
        lastFetchedIdRef.current = session.user.id;
        fetchProfile();
        fetchPoints();
    }, [session.user.id]);

    useEffect(() => {
        if (isOpen) {
            fetchPoints();
        }
    }, [isOpen]);

    const fetchProfile = async () => {
        setIsLoadingProfile(true);
        const { data } = await supabase
            .from('profiles')
            .select('display_name, level, level_expires_at, downgraded_at')
            .single();

        if (data) {
            if (data.display_name) setDisplayName(data.display_name);
            if (data.level) setLevel(data.level as RadarLevel);
            if (data.level_expires_at) setLevelExpiresAt(data.level_expires_at);
            if (data.downgraded_at) setDowngradedAt(data.downgraded_at);
        }
        setIsLoadingProfile(false);
    };

    const fetchPoints = async () => {
        const { data, error } = await supabase
            .from('my_points_balance')
            .select('balance_points')
            .maybeSingle();

        if (data) {
            setPoints(data.balance_points);
        } else if (error) {
            console.error('Error fetching points:', error);
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        setIsOpen(false);
        window.location.reload();
    };

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        setIsOpen(false);
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
            setCanInstall(false);
        }
        setDeferredPrompt(null);
    };

    const openProfile = () => {
        setIsOpen(false);
        setIsProfileOpen(true);
    };

    const email = session.user.email || 'Usuário';
    const initial = (displayName || email)[0].toUpperCase();
    const displayNameOrEmail = displayName || email.split('@')[0];

    return (
        <>
            <div className="relative" ref={menuRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-2 pl-2 pr-2 py-1.5 rounded-full glass-card hover:bg-white/50 dark:hover:bg-gray-800/50 border border-white/20 dark:border-white/5 transition-all group shadow-sm"
                >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold shadow-inner">
                        {initial}
                    </div>
                    <div className="text-left hidden sm:block">
                        <div className="flex items-center gap-1.5 mb-1">
                            {isLoadingProfile ? (
                                <div className="h-[18px] w-16 bg-white/30 dark:bg-gray-600/50 rounded-full animate-pulse"></div>
                            ) : (
                                <span className={`text-[10px] font-bold text-white px-1.5 py-0.5 rounded-full uppercase tracking-wider bg-gradient-to-r ${levelConfig[level].color} shadow-sm leading-none flex items-center gap-1.5`}>
                                    {React.cloneElement(levelConfig[level].icon as React.ReactElement, { className: 'w-2.5 h-2.5' })}
                                    {level}
                                </span>
                            )}
                        </div>
                        {isLoadingProfile ? (
                            <div className="h-3.5 w-20 bg-gray-200/50 dark:bg-gray-700/50 rounded animate-pulse mt-0.5"></div>
                        ) : (
                            <p className="text-sm font-bold text-gray-900 dark:text-white leading-none max-w-[100px] truncate">
                                {displayNameOrEmail}
                            </p>
                        )}
                    </div>
                </button>

                {isOpen && (
                    <div className="absolute right-0 mt-2 w-64 glass-card rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 overflow-hidden animate-in fade-in slide-in-from-top-2 z-50 bg-white dark:bg-gray-900">
                        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                            <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{displayNameOrEmail}</p>
                            <p className="text-xs text-gray-400 truncate">{email}</p>

                            <button
                                onClick={() => { setIsOpen(false); setIsStatementOpen(true); }}
                                className="w-full mt-3 flex items-center gap-3 px-3 py-2 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 rounded-xl hover:from-violet-500/20 hover:to-fuchsia-500/20 transition-all text-left group cursor-pointer"
                            >
                                <div className="p-1.5 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                                    <Receipt className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-violet-600 dark:text-violet-400 uppercase tracking-wider">Meus Pontos</p>
                                    <p className="text-sm font-black text-gray-900 dark:text-white leading-none mt-0.5">
                                        {points !== null ? points.toLocaleString('pt-BR') : '...'}
                                    </p>
                                </div>
                            </button>

                            <button
                                onClick={() => { setIsOpen(false); setIsLevelInfoOpen(true); }}
                                className="mt-2 w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                            >
                                <div className="flex flex-col items-start gap-0.5">
                                    <span className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 tracking-wider">Meu Nível Atual</span>
                                    <span className={`text-sm font-black capitalize ${levelConfig[level].textColor}`}>{level}</span>
                                </div>
                                <div className="flex items-end gap-2">
                                    <span className="text-[9px] text-gray-400 font-medium group-hover:text-blue-500 transition-colors mb-0.5 underline decoration-dotted underline-offset-2 mt-6">Saiba mais</span>
                                    <div className={`p-1.5 bg-gradient-to-br ${levelConfig[level].color} text-white rounded-lg shadow-sm group-hover:scale-110 transition-transform`}>
                                        {levelConfig[level].icon}
                                    </div>
                                </div>
                            </button>
                        </div>

                        <div className="p-2 space-y-1">
                            <button
                                onClick={openProfile}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/10 hover:text-blue-600 dark:hover:text-blue-400 rounded-xl transition-all group"
                            >
                                <User className="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                                Meu Perfil
                            </button>

                            <div className="h-px bg-gray-200 dark:bg-gray-800" />

                            <button
                                onClick={() => { setIsOpen(false); setIsHowToEarnPointsOpen(true); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/10 hover:text-yellow-600 dark:hover:text-yellow-400 rounded-xl transition-all group"
                            >
                                <Zap className="w-4 h-4 text-gray-400 group-hover:text-yellow-500" />
                                Como Ganhar Pontos?
                            </button>

                            <button
                                onClick={() => { setIsOpen(false); setIsWeeklyMissionsOpen(true); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-yellow-50 dark:hover:bg-yellow-900/10 hover:text-yellow-600 dark:hover:text-yellow-400 rounded-xl transition-all group"
                            >
                                <div className="flex items-center gap-3">
                                    <Target className="w-4 h-4 text-gray-400 group-hover:text-yellow-500 group-hover:scale-110 transition-transform" />
                                    Missões Semanais
                                </div>
                            </button>

                            <div className="h-px bg-gray-200 dark:bg-gray-800" />

                            <button
                                onClick={() => { setIsOpen(false); setIsStatementOpen(true); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/10 hover:text-purple-600 dark:hover:text-purple-400 rounded-xl transition-all group"
                            >
                                <Receipt className="w-4 h-4 text-gray-400 group-hover:text-purple-500" />
                                Extrato de Pontos
                            </button>

                            <button
                                onClick={() => { setIsOpen(false); setIsStoreOpen(true); }}
                                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-fuchsia-50 dark:hover:bg-fuchsia-900/10 hover:text-fuchsia-600 dark:hover:text-fuchsia-400 rounded-xl transition-all group border border-transparent hover:border-fuchsia-200 dark:hover:border-fuchsia-800/50"
                            >
                                <div className="flex items-center gap-3">
                                    <Store className="w-4 h-4 text-fuchsia-500 group-hover:scale-110 transition-transform" />
                                    Lojinha de Pontos
                                </div>
                                <span className="px-2 py-0.5 bg-fuchsia-100 dark:bg-fuchsia-900/50 text-[10px] font-bold text-fuchsia-600 dark:text-fuchsia-400 rounded-full">
                                    NOVO
                                </span>
                            </button>

                            <button
                                onClick={() => { setIsOpen(false); setIsRewardsHistoryOpen(true); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/10 hover:text-purple-600 dark:hover:text-purple-400 rounded-xl transition-all group"
                            >
                                <ShoppingBag className="w-4 h-4 text-gray-400 group-hover:text-purple-500" />
                                Meus Resgates
                            </button>

                            <div className="h-px bg-gray-200 dark:bg-gray-800" />

                            <button
                                onClick={() => { setIsOpen(false); setIsPointsRequestOpen(true); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-xl transition-all group"
                            >
                                <FilePlus className="w-4 h-4 text-gray-400 group-hover:text-emerald-500" />
                                Solicitar Pontos
                            </button>

                            <button
                                onClick={() => { setIsOpen(false); setIsPointsHistoryOpen(true); }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/10 hover:text-teal-600 dark:hover:text-teal-400 rounded-xl transition-all group"
                            >
                                <FileText className="w-4 h-4 text-gray-400 group-hover:text-teal-500" />
                                Minhas Solicitações
                            </button>

                            <div className="h-px bg-gray-200 dark:bg-gray-800" />

                            <button
                                onClick={() => { setIsOpen(false); setIsReferralOpen(true); }}
                                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-xl transition-all group border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800/50"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-5 flex justify-center">
                                        <Users className="w-4 h-4 text-indigo-500 group-hover:scale-110 transition-transform" />
                                    </div>
                                    Indique e Ganhe
                                </div>
                                <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 rounded-full">
                                    8K PTS
                                </span>
                            </button>

                            {canInstall && isMobile && (
                                <button
                                    onClick={handleInstallClick}
                                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/10 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-xl transition-all group border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800/50"
                                >
                                    <div className="flex items-center gap-3">
                                        <Download className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                        Baixar o App
                                    </div>
                                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-[10px] font-bold text-blue-600 dark:text-blue-400 rounded-full">
                                        RÁPIDO
                                    </span>
                                </button>
                            )}
                        </div>

                        <div className="p-2 border-t border-gray-100 dark:border-gray-800 mt-1">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors font-medium"
                            >
                                <LogOut className="w-4 h-4" />
                                Sair
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {isProfileOpen && (
                <ProfileModal
                    session={session}
                    isOpen={isProfileOpen}
                    onClose={() => setIsProfileOpen(false)}
                    onProfileUpdate={fetchProfile}
                />
            )}

            {isStatementOpen && (
                <PointsStatementModal
                    isOpen={isStatementOpen}
                    onClose={() => setIsStatementOpen(false)}
                />
            )}

            {isPointsRequestOpen && (
                <PointsEarningRequestModal
                    session={session}
                    isOpen={isPointsRequestOpen}
                    onClose={() => setIsPointsRequestOpen(false)}
                    onViewRequests={() => {
                        setIsPointsRequestOpen(false);
                        setIsPointsHistoryOpen(true);
                    }}
                />
            )}

            {isPointsHistoryOpen && (
                <PointsEarningHistoryModal
                    isOpen={isPointsHistoryOpen}
                    onClose={() => setIsPointsHistoryOpen(false)}
                />
            )}

            {isStoreOpen && (
                <RewardsStoreModal
                    session={session}
                    isOpen={isStoreOpen}
                    onClose={() => setIsStoreOpen(false)}
                    onOpenHistory={() => {
                        setIsStoreOpen(false);
                        setIsRewardsHistoryOpen(true);
                    }}
                />
            )}

            {isRewardsHistoryOpen && (
                <RewardsHistoryModal
                    isOpen={isRewardsHistoryOpen}
                    onClose={() => setIsRewardsHistoryOpen(false)}
                />
            )}

            {isLevelInfoOpen && (
                <LevelInfoModal
                    isOpen={isLevelInfoOpen}
                    onClose={() => setIsLevelInfoOpen(false)}
                    currentLevel={level}
                    levelExpiresAt={levelExpiresAt}
                    downgradedAt={downgradedAt}
                />
            )}

            {isReferralOpen && (
                <ReferralModal
                    session={session}
                    isOpen={isReferralOpen}
                    onClose={() => setIsReferralOpen(false)}
                />
            )}

            {isHowToEarnPointsOpen && (
                <HowToEarnPointsModal
                    isOpen={isHowToEarnPointsOpen}
                    onClose={() => setIsHowToEarnPointsOpen(false)}
                />
            )}

            {isWeeklyMissionsOpen && (
                <WeeklyMissionsModal
                    isOpen={isWeeklyMissionsOpen}
                    onClose={() => setIsWeeklyMissionsOpen(false)}
                    session={session}
                />
            )}
        </>
    );
};
